
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { X, Plus } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import QuestionBankDropdown from "@/components/shared/QuestionBankDropdown";

const AutoSelectionSidebar = ({
  newAutoSelection,
  setNewAutoSelection,
  autoSelections,
  categoriesData,
  handleAutoSelectionAdd,
  handleAutoSelectionRemove,
  handleAutoSelectQuestions,
}) => {
  const handleQuestionBankChange = (selectedBankIds) => {
    setNewAutoSelection((prev) => ({
      ...prev,
      questionBankIds: selectedBankIds
    }));
  };

  const handleDifficultyChange = (difficulty, value) => {
    setNewAutoSelection((prev) => ({
      ...prev,
      difficulties: {
        ...prev.difficulties,
        [difficulty]: Math.max(0, parseInt(value) || 0)
      }
    }));
  };

  return (
    <div className="w-80 border-r bg-gray-50/50 h-full flex flex-col">
      <Card className="border-0 bg-transparent shadow-none flex-1 flex flex-col">
        <CardHeader className="pb-4 flex-shrink-0">
          <CardTitle className="text-lg">Auto Selection</CardTitle>
        </CardHeader>
        <CardContent className="flex-1 overflow-hidden">
          <ScrollArea className="h-full pr-4">
            <div className="space-y-6">
              {/* New Auto Selection Form */}
              <div className="space-y-4 p-4 border rounded-lg bg-white">
                <h4 className="font-medium text-sm">Add Selection Criteria</h4>
                
                <div className="space-y-2">
                  <Label className="text-sm">Question Banks</Label>
                  <QuestionBankDropdown
                    selectedBankIds={newAutoSelection.questionBankIds}
                    onSelectionChange={handleQuestionBankChange}
                    userQuestionBanks={categoriesData?.data?.userQuestionBanks || []}
                    companyQuestionBanks={categoriesData?.data?.companyQuestionBanks || []}
                    placeholder="Select Question Banks..."
                    multiple={true}
                  />
                </div>

                <div className="space-y-3">
                  <Label className="text-sm">Number of Questions by Difficulty</Label>
                  <div className="grid grid-cols-1 gap-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-green-700">Easy:</span>
                      <Input
                        type="number"
                        min="0"
                        className="w-16 h-8"
                        value={newAutoSelection.difficulties.easy}
                        onChange={(e) => handleDifficultyChange('easy', e.target.value)}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-yellow-700">Medium:</span>
                      <Input
                        type="number"
                        min="0"
                        className="w-16 h-8"
                        value={newAutoSelection.difficulties.medium}
                        onChange={(e) => handleDifficultyChange('medium', e.target.value)}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-red-700">Hard:</span>
                      <Input
                        type="number"
                        min="0"
                        className="w-16 h-8"
                        value={newAutoSelection.difficulties.hard}
                        onChange={(e) => handleDifficultyChange('hard', e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                <Button 
                  onClick={handleAutoSelectionAdd} 
                  size="sm" 
                  className="w-full"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Criteria
                </Button>
              </div>

              {/* Auto Selection List */}
              {autoSelections.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-medium text-sm">Selection Criteria</h4>
                  <div className="space-y-2">
                    {autoSelections.map((selection) => (
                      <div key={selection.id} className="p-3 border rounded-lg bg-white">
                        <div className="flex items-start justify-between mb-2">
                          <div className="text-xs font-medium">Criteria #{selection.id}</div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleAutoSelectionRemove(selection.id)}
                            className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                        <div className="space-y-1 text-xs">
                          <div>Banks: {selection.questionBankIds.length}</div>
                          <div className="flex gap-1 flex-wrap">
                            {selection.difficulties.easy > 0 && (
                              <Badge variant="outline" className="text-xs bg-green-100 text-green-800">
                                Easy: {selection.difficulties.easy}
                              </Badge>
                            )}
                            {selection.difficulties.medium > 0 && (
                              <Badge variant="outline" className="text-xs bg-yellow-100 text-yellow-800">
                                Medium: {selection.difficulties.medium}
                              </Badge>
                            )}
                            {selection.difficulties.hard > 0 && (
                              <Badge variant="outline" className="text-xs bg-red-100 text-red-800">
                                Hard: {selection.difficulties.hard}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <Button 
                    onClick={handleAutoSelectQuestions}
                    className="w-full mt-4"
                    disabled={autoSelections.length === 0}
                  >
                    Auto Select Questions
                  </Button>
                </div>
              )}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
};

export default AutoSelectionSidebar;